import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Divider,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Close,
  Add,
  Delete,
  CloudUpload,
  Download,
  PersonAdd,
  UploadFile,
  CheckCircle,
} from '@mui/icons-material';
import { api } from '../context/AuthContext';

// ── CSV template ────────────────────────────────────────────────────────────
const CSV_TEMPLATE = 'first_name,last_name,email,phone\nJohn,Doe,john@example.com,9876543210\n';

// ── Empty candidate row ──────────────────────────────────────────────────────
const emptyCandidate = () => ({ id: Date.now() + Math.random(), firstName: '', lastName: '', email: '', phone: '' });

// ── Skill tag input ─────────────────────────────────────────────────────────
function SkillsInput({ skills, onChange }) {
  const [input, setInput] = useState('');

  const addSkill = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    if (skills.includes(trimmed)) { setInput(''); return; }
    onChange([...skills, trimmed]);
    setInput('');
  };

  const removeSkill = (s) => onChange(skills.filter((x) => x !== s));

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
          placeholder="Type a skill and press Enter"
          size="small"
          fullWidth
        />
        <Button variant="outlined" onClick={addSkill} sx={{ whiteSpace: 'nowrap', minWidth: 90 }}>
          Add Skill
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {skills.map((s) => (
          <Chip
            key={s}
            label={s}
            onDelete={() => removeSkill(s)}
            size="small"
            sx={{ bgcolor: '#E0F2FE', color: '#0369A1', fontWeight: 600 }}
          />
        ))}
      </Box>
    </Box>
  );
}

// ── Main Modal ───────────────────────────────────────────────────────────────
export default function ScheduleDirectInterviewModal({ open, onClose, onSuccess }) {
  // Section 1 — Interview Details
  const [jobTitle, setJobTitle] = useState('');
  const [skills, setSkills] = useState([]);
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [interviewDuration, setInterviewDuration] = useState(30);
  const [resumeFile, setResumeFile]   = useState(null);
  const [resumeError, setResumeError] = useState('');

  const [questionBankFile, setQuestionBankFile] = useState(null);
  const [questionBankError, setQuestionBankError] = useState('');
  const [questionCount, setQuestionCount] = useState(0);

  // Section 2 — Candidates
  const [candidates, setCandidates] = useState([emptyCandidate()]);
  const [csvError, setCsvError]     = useState('');
  const csvInputRef  = useRef();
  const fileInputRef = useRef();
  const questionBankInputRef = useRef();

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError]   = useState('');

  // ── Reset ────────────────────────────────────────────────────────────────
  const resetForm = () => {
    setJobTitle('');
    setSkills([]);
    setInterviewDate('');
    setInterviewTime('');
    setInterviewDuration(30);
    setResumeFile(null);
    setResumeError('');
    setQuestionBankFile(null);
    setQuestionBankError('');
    setQuestionCount(0);
    setCandidates([emptyCandidate()]);
    setCsvError('');
    setFormError('');
  };

  const handleClose = () => {
    if (!submitting) { resetForm(); onClose(); }
  };

  // ── Resume upload validation ─────────────────────────────────────────────
  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    setResumeError('');
    if (!file) return;
    const allowed = ['application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      setResumeError('Only PDF, DOC, or DOCX files are allowed.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setResumeError('File size must not exceed 10 MB.');
      return;
    }
    setResumeFile(file);
  };

  const handleQuestionBankChange = (e) => {
    const file = e.target.files[0];
    setQuestionBankError('');
    setQuestionCount(0);
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.json')) {
      setQuestionBankError('Only .json files are allowed.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setQuestionBankError('File size must not exceed 10 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = JSON.parse(evt.target.result);
        let questions = [];
        if (Array.isArray(data)) {
          questions = data;
        } else if (data && Array.isArray(data.questions)) {
          questions = data.questions;
        }

        if (questions.length === 0) {
          setQuestionBankError('JSON bank must contain an array of questions.');
          return;
        }
        if (questions.length > 5000) {
          setQuestionBankError('JSON bank cannot exceed 5000 questions.');
          return;
        }

        setQuestionCount(questions.length);
        setQuestionBankFile(file);
        setQuestionBankError('');
      } catch (err) {
        setQuestionBankError('Invalid JSON format.');
      }
    };
    reader.readAsText(file);
  };

  const removeQuestionBank = () => {
    setQuestionBankFile(null);
    setQuestionCount(0);
    setQuestionBankError('');
    if (questionBankInputRef.current) questionBankInputRef.current.value = '';
  };

  // ── Candidate row management ─────────────────────────────────────────────
  const addRow = () => setCandidates((prev) => [...prev, emptyCandidate()]);

  const removeRow = (id) =>
    setCandidates((prev) => prev.length > 1 ? prev.filter((c) => c.id !== id) : prev);

  const updateRow = (id, field, value) =>
    setCandidates((prev) => prev.map((c) => c.id === id ? { ...c, [field]: value } : c));

  // ── CSV parse ────────────────────────────────────────────────────────────
  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const lines = evt.target.result.trim().split('\n');
        if (lines.length < 2) { setCsvError('CSV must have a header and at least one data row.'); return; }
        const header = lines[0].toLowerCase().split(',').map((h) => h.trim());
        const idxFn  = (k) => header.indexOf(k);
        const fi = idxFn('first_name'), li = idxFn('last_name'), ei = idxFn('email'), pi = idxFn('phone');
        if (fi < 0 || ei < 0) { setCsvError('CSV must have first_name and email columns.'); return; }

        const emailsSeen = new Set(candidates.map((c) => c.email.toLowerCase()).filter(Boolean));
        const newRows = [];
        const errors = [];

        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
          const email = (cols[ei] || '').toLowerCase();
          const firstName = cols[fi] || '';
          if (!email || !firstName) { errors.push(`Row ${i + 1}: missing first_name or email.`); continue; }
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { errors.push(`Row ${i + 1}: invalid email "${email}".`); continue; }
          if (emailsSeen.has(email)) { errors.push(`Row ${i + 1}: duplicate email "${email}" — skipped.`); continue; }
          emailsSeen.add(email);
          newRows.push({ id: Date.now() + i, firstName, lastName: li >= 0 ? (cols[li] || '') : '', email: cols[ei] || '', phone: pi >= 0 ? (cols[pi] || '') : '' });
        }

        if (errors.length) { setCsvError(errors.join('\n')); }
        else { setCsvError(''); }

        if (newRows.length) {
          setCandidates((prev) => {
            // Remove blank placeholder rows first
            const nonEmpty = prev.filter((c) => c.email || c.firstName);
            return [...nonEmpty, ...newRows];
          });
        }
      } catch {
        setCsvError('Failed to parse CSV. Please check the format.');
      }
    };
    reader.readAsText(file);
  };

  // ── CSV template download ────────────────────────────────────────────────
  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'direct_interview_candidates.csv';
    a.click(); URL.revokeObjectURL(url);
  };

  // ── Validate form ────────────────────────────────────────────────────────
  const validate = () => {
    if (!jobTitle.trim()) return 'Job title is required.';
    
    // Exactly one of the following must be provided: Primary Skills OR Question Bank JSON File
    const hasSkills = skills.length > 0;
    const hasQuestionBank = !!questionBankFile;
    if (!hasSkills && !hasQuestionBank) {
      return 'Please provide either Primary Skills or a Question Bank JSON.';
    }

    if (!interviewDate) return 'Interview date is required.';
    if (!interviewTime) return 'Interview time is required.';
    const dur = Number(interviewDuration);
    if (!Number.isInteger(dur) || dur < 0 || dur > 45)
      return 'Please enter a valid interview duration between 0 and 45 minutes.';

    const validCandidates = candidates.filter((c) => c.firstName.trim() || c.email.trim());
    if (validCandidates.length === 0) return 'At least one candidate is required.';

    const emailSet = new Set();
    for (const c of validCandidates) {
      if (!c.firstName.trim()) return `First name is required for candidate with email "${c.email}".`;
      if (!c.email.trim()) return 'Email is required for all candidates.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email.trim())) return `Invalid email: "${c.email}".`;

      if (c.phone.trim()) {
        const digits = c.phone.replace(/\D/g, '');
        if (digits.length < 10) return `Phone number must be at least 10 digits for candidate "${c.firstName}".`;
      }

      const key = c.email.trim().toLowerCase();
      if (emailSet.has(key)) return `Duplicate email detected: "${c.email}". Please remove duplicates.`;
      emailSet.add(key);
    }
    return null;
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const err = validate();
    if (err) { setFormError(err); return; }
    setFormError('');
    setSubmitting(true);

    try {
      // Combine date + time → ISO string
      const scheduledAt = new Date(`${interviewDate}T${interviewTime}:00`).toISOString();

      const validCandidates = candidates
        .filter((c) => c.firstName.trim() && c.email.trim())
        .map((c) => ({
          firstName: c.firstName.trim(),
          lastName: c.lastName.trim(),
          email: c.email.trim(),
          phone: c.phone.trim(),
        }));

      const payload = {
        jobTitle: jobTitle.trim(),
        primarySkills: skills,
        scheduledAt,
        interviewDuration: Number(interviewDuration),
        resumeUrl: null, // resume upload handled separately if needed
        candidates: validCandidates,
        questionBankJson: null
      };

      if (questionBankFile) {
        const fileContent = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = (e) => reject(e);
          reader.readAsText(questionBankFile);
        });
        payload.questionBankJson = fileContent;
      }

      const { data } = await api.post('/direct-interviews/schedule', payload);

      resetForm();
      onSuccess(data);
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to schedule interviews. Please try again.';
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Deduplicate manually ─────────────────────────────────────────────────
  const deduplicateEmails = useCallback(() => {
    const seen = new Set();
    setCandidates((prev) =>
      prev.filter((c) => {
        const key = c.email.trim().toLowerCase();
        if (!key) return true; // keep blank rows
        if (seen.has(key)) return false;
        seen.add(key); return true;
      })
    );
  }, []);

  const today = new Date().toISOString().split('T')[0];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}
    >
      {submitting && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, borderRadius: '12px 12px 0 0' }} />}

      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Schedule Direct Interview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Schedule AI interviews without a job application
          </Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={submitting} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {formError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setFormError('')}>
            {formError}
          </Alert>
        )}

        {/* ── Section 1: Interview Details ─────────────────────────────── */}
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: 'primary.main' }}>
          Section 1 — Interview Details
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {/* Job Title */}
          <TextField
            label="Job Title *"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. Senior Java Developer"
            disabled={submitting}
          />

          {/* Primary Skills */}
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, color: 'text.secondary' }}>
              Primary Skills
            </Typography>
            <SkillsInput skills={skills} onChange={setSkills} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ mx: 2, color: 'text.secondary', fontWeight: 700 }}>
              OR
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          {/* Question Bank (Optional JSON) */}
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, color: 'text.secondary' }}>
              Question Bank (Optional JSON)
            </Typography>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: questionBankError ? 'error.main' : 'divider',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                bgcolor: 'action.hover',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.selected' }
              }}
              onClick={() => !questionBankFile && questionBankInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const file = e.dataTransfer.files[0];
                if (file) {
                  const event = { target: { files: [file] } };
                  handleQuestionBankChange(event);
                }
              }}
            >
              <input
                type="file"
                accept=".json"
                ref={questionBankInputRef}
                style={{ display: 'none' }}
                onChange={handleQuestionBankChange}
              />
              {!questionBankFile ? (
                <>
                  <UploadFile sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    <b>Click to browse</b> or drag & drop JSON question bank
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Maximum size: 10 MB
                  </Typography>
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CheckCircle color="success" />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" fontWeight={700}>
                      {questionBankFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {questionCount} Questions Loaded
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeQuestionBank(); }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
            {questionBankError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {questionBankError}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
              "Upload a JSON file containing interview questions. If provided, interview questions will be selected randomly from this file instead of AI-generated skill-based questions."
            </Typography>
          </Box>

          {/* Date + Time row */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Interview Date *"
              type="date"
              value={interviewDate}
              onChange={(e) => setInterviewDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today }}
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
              disabled={submitting}
            />
            <TextField
              label="Interview Time *"
              type="time"
              value={interviewTime}
              onChange={(e) => setInterviewTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
              disabled={submitting}
            />
          </Box>

          {/* Total Interview Duration */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              label="Total Interview Duration *"
              type="number"
              value={interviewDuration}
              onChange={(e) => setInterviewDuration(e.target.value)}
              inputProps={{ min: 0, max: 45, step: 1 }}
              size="small"
              sx={{ width: 200 }}
              disabled={submitting}
            />
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Minutes &nbsp;<span style={{ color: '#94A3B8' }}>(0 – 45)</span>
            </Typography>
          </Box>

          {/* Resume Upload (optional) */}
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, color: 'text.secondary' }}>
              Resume (Optional) — PDF, DOC, DOCX · Max 10 MB
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleResumeChange}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
              >
                Upload Resume
              </Button>
              {resumeFile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip label={resumeFile.name} size="small" sx={{ maxWidth: 240 }} />
                  <IconButton size="small" onClick={() => setResumeFile(null)}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
            {resumeError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {resumeError}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* ── Section 2: Candidates ─────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'primary.main' }}>
            Section 2 — Candidates
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Download CSV template">
              <Button
                variant="text"
                size="small"
                startIcon={<Download />}
                onClick={downloadTemplate}
                sx={{ fontSize: '0.75rem' }}
              >
                Template
              </Button>
            </Tooltip>
            <input
              type="file"
              accept=".csv"
              ref={csvInputRef}
              style={{ display: 'none' }}
              onChange={handleCSVUpload}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudUpload />}
              onClick={() => csvInputRef.current?.click()}
              disabled={submitting}
            >
              Upload CSV
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAdd />}
              onClick={addRow}
              disabled={submitting}
            >
              + Add Candidate
            </Button>
          </Box>
        </Box>

        {csvError && (
          <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2, whiteSpace: 'pre-wrap' }}>
            {csvError}
          </Alert>
        )}

        {/* Duplicate warning */}
        {(() => {
          const emails = candidates.map((c) => c.email.trim().toLowerCase()).filter(Boolean);
          const hasDups = emails.length !== new Set(emails).size;
          return hasDups ? (
            <Alert
              severity="warning"
              sx={{ mb: 1.5, borderRadius: 2 }}
              action={
                <Button size="small" color="inherit" onClick={deduplicateEmails}>
                  Remove Duplicates
                </Button>
              }
            >
              Duplicate email addresses detected in the candidate list.
            </Alert>
          ) : null;
        })()}

        {/* Candidates table */}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#F8FAFC', fontSize: '0.78rem' } }}>
                <TableCell>First Name *</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Email *</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {candidates.map((c) => (
                <TableRow key={c.id} sx={{ '&:hover': { bgcolor: '#FAFAFA' } }}>
                  <TableCell sx={{ py: 0.5 }}>
                    <TextField
                      value={c.firstName}
                      onChange={(e) => updateRow(c.id, 'firstName', e.target.value)}
                      size="small"
                      variant="standard"
                      placeholder="First name"
                      disabled={submitting}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <TextField
                      value={c.lastName}
                      onChange={(e) => updateRow(c.id, 'lastName', e.target.value)}
                      size="small"
                      variant="standard"
                      placeholder="Last name"
                      disabled={submitting}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <TextField
                      value={c.email}
                      onChange={(e) => updateRow(c.id, 'email', e.target.value)}
                      size="small"
                      variant="standard"
                      placeholder="email@example.com"
                      type="email"
                      disabled={submitting}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <TextField
                      value={c.phone}
                      onChange={(e) => updateRow(c.id, 'phone', e.target.value)}
                      size="small"
                      variant="standard"
                      placeholder="Phone number"
                      disabled={submitting}
                      fullWidth
                      inputProps={{ maxLength: 15 }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => removeRow(c.id)}
                      disabled={submitting || candidates.length === 1}
                      sx={{ color: 'error.main' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={addRow}
            disabled={submitting}
            sx={{ color: 'primary.main' }}
          >
            Add another candidate
          </Button>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ minWidth: 180 }}
        >
          {submitting ? 'Scheduling…' : 'Schedule Interviews'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
